export const MASTER_SOCIAL_LINK_PLATFORMS = [
  'website',
  'instagram',
  'youtube',
  'telegram',
  'rutube',
  'dzen',
  'vk',
] as const;

export type MasterSocialLinkPlatform = typeof MASTER_SOCIAL_LINK_PLATFORMS[number];

export const RU_RESTRICTED_SOCIAL_MATCHERS = [
  'instagram',
  'facebook',
  'threads',
  'linkedin',
  'patreon',
  'discord',
  'viber',
  'signal',
  'roblox',
  'youtube',
] as const;

export interface MasterProfileSocialLink {
  platform: MasterSocialLinkPlatform;
  url: string;
}

export interface MasterSocialLinkFields {
  website: string;
  instagram: string;
  youtube: string;
  telegram: string;
  rutube: string;
  dzen: string;
  vk: string;
}

const emptyFields = (): MasterSocialLinkFields => ({
  website: '',
  instagram: '',
  youtube: '',
  telegram: '',
  rutube: '',
  dzen: '',
  vk: '',
});

const normalizePlatform = (value: unknown): MasterSocialLinkPlatform | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'site' || normalized === 'web' || normalized === 'сайт') return 'website';
  if (normalized === 'zen' || normalized === 'yandex-zen' || normalized === 'yandex_dzen' || normalized === 'яндекс-дзен') return 'dzen';
  if (normalized === 'vkontakte' || normalized === 'вк' || normalized === 'вконтакте') return 'vk';
  return MASTER_SOCIAL_LINK_PLATFORMS.includes(normalized as MasterSocialLinkPlatform)
    ? normalized as MasterSocialLinkPlatform
    : null;
};

export function parseMasterProfileSocialLinks(data: unknown): MasterProfileSocialLink[] {
  const source = Array.isArray(data)
    ? data
    : typeof data === 'object' && data !== null && 'socialLinks' in data
      ? (data as { socialLinks?: unknown }).socialLinks
      : [];

  if (!Array.isArray(source)) return [];

  const byPlatform = new Map<MasterSocialLinkPlatform, MasterProfileSocialLink>();
  source.forEach((entry) => {
    if (typeof entry !== 'object' || entry === null) return;
    const platform = normalizePlatform((entry as { platform?: unknown }).platform);
    const rawUrl = (entry as { url?: unknown }).url;
    const url = typeof rawUrl === 'string' ? rawUrl.trim() : '';
    if (platform && url) byPlatform.set(platform, { platform, url });
  });

  return MASTER_SOCIAL_LINK_PLATFORMS.flatMap((platform) => {
    const socialLink = byPlatform.get(platform);
    return socialLink ? [socialLink] : [];
  });
}

export function masterSocialLinksToFields(
  socialLinks: readonly MasterProfileSocialLink[],
): MasterSocialLinkFields {
  const fields = emptyFields();
  socialLinks.forEach(({ platform, url }) => {
    fields[platform] = url;
  });
  return fields;
}

export function masterSocialLinkFieldsToList(
  fields: MasterSocialLinkFields,
): MasterProfileSocialLink[] {
  return MASTER_SOCIAL_LINK_PLATFORMS.flatMap((platform) => {
    const url = fields[platform].trim();
    return url ? [{ platform, url }] : [];
  });
}
